<template>
  <SettingsRow name="Export calls" description="Whether calls dialed here are written back to your CRM.">
    <div class="state">
      <StatusChip v-if="!loaded" tone="neutral" label="…" />
      <template v-else>
        <!-- The master switch also drives full-product contact/email export and
             enrollment gating, so only dialer-only admins get a toggle here;
             full-product admins keep ExportTab, where that context lives. -->
        <ToggleSwitch
          v-if="canToggle"
          :model-value="enabled"
          :disabled="busy"
          @update:model-value="onToggle"
        />
        <StatusChip v-if="enabled && provider" tone="success" label="✓ On" />
        <StatusChip v-else-if="enabled" tone="amber" label="Check connection" />
        <StatusChip v-else tone="neutral" label="Off" />
      </template>
    </div>
    <p class="copy">
      <template v-if="loaded">
        <template v-if="enabled && provider">{{ onCopy }}</template>
        <template v-else-if="enabled">Export is on, but the CRM connection isn’t active right now.</template>
        <template v-else-if="provider">{{ offWithProviderCopy }}</template>
        <template v-else>Connect your CRM from the integration settings page first.</template>
      </template>
      <template v-else>Checking whether calls are exported to your CRM…</template>
    </p>
    <p v-if="error" class="errLine">{{ error }}</p>
    <p v-else-if="savedFlash" class="flash">Saved</p>
  </SettingsRow>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import ToggleSwitch from '../../../../components/ToggleSwitch.vue'
import { useCrmExport } from '../../../composables/useCrmExport'
import { useTelephony } from '../../../composables/useTelephony'
import { useCurrentUser } from '../../../../services/user'
import SettingsRow from './SettingsRow.vue'
import StatusChip from './StatusChip.vue'

const { isAdmin } = useTelephony()
const { isDialerOnly } = useCurrentUser()
const crmExport = useCrmExport()

const loaded = crmExport.loaded
const enabled = crmExport.enabled
const provider = crmExport.provider

const busy = ref(false)
const error = ref('')
const savedFlash = ref(false)

const providerLabel = computed(
  () => ({ salesforce: 'Salesforce', hubspot: 'HubSpot' })[provider.value || ''] || provider.value,
)

// Enabling needs a resolvable target; with none there is nothing to switch on.
const canToggle = computed(() => isDialerOnly.value && isAdmin.value && Boolean(enabled.value || provider.value))

const onCopy = computed(() =>
  provider.value === 'salesforce'
    ? 'Calls you log here are exported to Salesforce as Tasks.'
    : `Calls you log here are exported to ${providerLabel.value} as call engagements.`,
)

const offWithProviderCopy = computed(() => {
  if (canToggle.value) return `Turn this on to log your calls in ${providerLabel.value}.`
  if (isAdmin.value && !isDialerOnly.value) return 'Turn this on under Integrations.'
  return 'Your admin can turn this on.'
})

async function onToggle(next: boolean) {
  if (busy.value) return
  busy.value = true
  error.value = ''
  try {
    await crmExport.setEnabled(next)
    savedFlash.value = true
    setTimeout(() => (savedFlash.value = false), 1500)
  } catch (e) {
    console.warn('[CrmExportCard] toggle failed', e)
    // setEnabled already refreshed, so the toggle reflects server truth again.
    error.value = 'Couldn’t update this — try again shortly.'
  } finally {
    busy.value = false
  }
}

onMounted(() => {
  void crmExport.refresh()
})
</script>

<style scoped>
.state { display: flex; align-items: center; gap: var(--space-3); }
.copy { margin: 0; font-size: var(--fs-sm); line-height: var(--lh-normal); color: var(--color-text-secondary); }
.errLine { margin: 0; font-size: var(--fs-xs); color: var(--color-danger); }
.flash { margin: 0; font-size: var(--fs-xs); color: var(--color-success); }
</style>
